import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Field, inputCls } from "@/components/ui-primitives";
import { Trash2, Minus, Plus, Search } from "lucide-react";

const emptyForm = {
  part_no: "", description: "", machine: "", qty: 0, min_qty: 1, supplier: "",
};

export default function Parts() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState("");
  const [lowOnly, setLowOnly] = useState(false);

  const load = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (lowOnly) params.low_only = "true";
      const { data } = await api.get("/parts", { params });
      setItems(data);
    } catch { toast.error("Failed to load"); }
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, lowOnly]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) { toast.error("Description is required"); return; }
    try {
      await api.post("/parts", { ...form, qty: Number(form.qty), min_qty: Number(form.min_qty) });
      toast.success("Part added");
      setForm(emptyForm);
      load();
    } catch { toast.error("Add failed"); }
  };

  const adjust = async (part, delta) => {
    const newQty = Math.max(0, Number(part.qty) + delta);
    try {
      await api.put(`/parts/${part.id}`, { qty: newQty });
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id, desc) => {
    if (!window.confirm(`Delete "${desc}"?`)) return;
    try {
      await api.delete(`/parts/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="p-8 max-w-[1800px] mx-auto">
      <PageHeader eyebrow="Inventory" title="Spare" accent="Parts" />

      <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 bg-[#121212] border border-white/10 p-5 mb-6">
        <Field label="Part No.">
          <input data-testid="part-no" value={form.part_no} onChange={(e) => setForm({ ...form, part_no: e.target.value })} className={inputCls} />
        </Field>
        <Field label="Description *">
          <input data-testid="part-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} />
        </Field>
        <Field label="Machine">
          <input data-testid="part-machine" value={form.machine} onChange={(e) => setForm({ ...form, machine: e.target.value })} className={inputCls} />
        </Field>
        <Field label="Supplier">
          <input data-testid="part-supplier" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} className={inputCls} />
        </Field>
        <Field label="Quantity">
          <input type="number" min="0" data-testid="part-qty" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} className={inputCls} />
        </Field>
        <Field label="Minimum Quantity">
          <input type="number" min="0" data-testid="part-min" value={form.min_qty} onChange={(e) => setForm({ ...form, min_qty: e.target.value })} className={inputCls} />
        </Field>
        <div className="flex items-end lg:col-span-2">
          <button data-testid="part-add-btn" type="submit" className="w-full px-5 py-2.5 bg-[#007AFF] hover:bg-[#3395FF] text-white text-sm font-medium">
            Add Part
          </button>
        </div>
      </form>

      {/* Search & filter */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
          <input
            data-testid="part-search"
            placeholder="Search part no, description, machine, supplier..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-transparent border border-white/20 pl-10 pr-3 py-2 text-sm text-white placeholder:text-[#737373] focus:outline-none focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF]"
          />
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            data-testid="part-low-only"
            type="checkbox"
            checked={lowOnly}
            onChange={(e) => setLowOnly(e.target.checked)}
            className="accent-[#F5A623] w-4 h-4"
          />
          <span className="text-xs text-[#E0E0E0]">Low stock only</span>
        </label>
        <div className="font-mono-tab text-[10px] tracking-[0.2em] uppercase text-[#737373]">
          {items.length} result{items.length === 1 ? "" : "s"}
        </div>
      </div>

      <div className="border border-white/10 bg-[#121212] overflow-x-auto">
        <table data-testid="parts-table" className="w-full text-sm">
          <thead className="bg-black/60 backdrop-blur-xl">
            <tr className="text-left font-mono-tab text-[10px] tracking-[0.2em] uppercase text-[#737373]">
              <th className="px-4 py-3 border-b border-white/10">Part No.</th>
              <th className="px-4 py-3 border-b border-white/10">Description</th>
              <th className="px-4 py-3 border-b border-white/10">Machine</th>
              <th className="px-4 py-3 border-b border-white/10 text-center">Qty</th>
              <th className="px-4 py-3 border-b border-white/10 text-right">Min</th>
              <th className="px-4 py-3 border-b border-white/10">Supplier</th>
              <th className="px-4 py-3 border-b border-white/10">Status</th>
              <th className="px-4 py-3 border-b border-white/10 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (<tr><td colSpan={8} className="px-4 py-12 text-center text-[#A3A3A3]">No parts match.</td></tr>)}
            {items.map((p) => {
              const low = Number(p.qty) <= Number(p.min_qty);
              return (
                <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.03]">
                  <td className="px-4 py-3 font-mono-tab text-xs text-[#E0E0E0]">{p.part_no || "—"}</td>
                  <td className="px-4 py-3 text-white">{p.description}</td>
                  <td className="px-4 py-3 text-[#E0E0E0]">{p.machine || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button data-testid={`part-dec-${p.id}`} onClick={() => adjust(p, -1)} className="p-1 border border-white/10 hover:border-white/30 hover:bg-white/5">
                        <Minus className="w-3 h-3 text-[#E0E0E0]" />
                      </button>
                      <span className="font-mono-tab text-sm text-white w-8 text-center">{p.qty}</span>
                      <button data-testid={`part-inc-${p.id}`} onClick={() => adjust(p, 1)} className="p-1 border border-white/10 hover:border-white/30 hover:bg-white/5">
                        <Plus className="w-3 h-3 text-[#E0E0E0]" />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono-tab text-xs text-[#A3A3A3]">{p.min_qty}</td>
                  <td className="px-4 py-3 text-[#E0E0E0]">{p.supplier || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 text-[10px] font-mono-tab tracking-[0.15em] uppercase border ${
                      low ? "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/30" : "bg-[#22C55E]/15 text-[#22C55E] border-[#22C55E]/30"
                    }`}>
                      {low ? "LOW" : "OK"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end">
                      <button data-testid={`part-del-${p.id}`} onClick={() => remove(p.id, p.description)} className="p-1.5 border border-white/10 hover:border-[#FF3B30]/40 hover:bg-[#FF3B30]/10">
                        <Trash2 className="w-3.5 h-3.5 text-[#FF3B30]" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
